import React, { useCallback, useMemo } from "react";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import type { TickerChartDataPoint } from "@bloomberg/media-ui-markets";
import {
  useComparisonData,
  useSelectedLegends,
  useComparisonSettings,
  WatchlistContext,
} from "~context/WatchlistContext";
import { getChartTickerColor } from "~utils/portfolio/getChartColors";
import type { TickerChartLegendData } from "~types/types";
import TickerChartLegend from "~components/TickerChartLegend";

type ComparisonChartLegendProps = {
  readonly className?: string;
  readonly enableTickerRemoval?: boolean;
  readonly applyInteractiveStyles?: boolean;
  readonly hoveredDataPoint?: TickerChartDataPoint;
};

export default function ComparisonChartLegend(
  props: ComparisonChartLegendProps = {}
) {
  const {
    className,
    enableTickerRemoval = false,
    applyInteractiveStyles = false,
    hoveredDataPoint,
  } = props;

  // ✅ ALL HOOKS FIRST (before any returns)
  const comparisonData = useComparisonData();
  const selectedLegends = useSelectedLegends();
  const comparisonSettings = useComparisonSettings();
  const store = React.useContext(WatchlistContext);

  // ✅ Define callbacks BEFORE early return
  const handleTickerSelectionChange = useCallback(
    (tickerId: string, isSelected: boolean) => {
      if (!store) return;
      const { toggleChartLegend } = store.getState();

      toggleChartLegend(tickerId);
    },
    [store]
  );

  const handleTickerRemoval = useCallback(
    (tickerId: string) => {
      if (!store || !comparisonSettings) return;

      trackClickEvent(
        "watchlistComparisonRemove",
        "remove_comparison",
        "click",
        "watchlist"
      );

      // Find the comparison by ticker ID
      const comparison = comparisonSettings.comparisons.find(
        (c) =>
          c.comparisonTicker === tickerId ||
          c.comparisonWatchlistId?.toString() === tickerId
      );

      if (!comparison) return;

      const { updateComparisonSettings } = store.getState();
      const updatedComparisons = comparisonSettings.comparisons.filter(
        (c) => c.compareId !== comparison.compareId
      );

      void updateComparisonSettings(updatedComparisons);
    },
    [store, comparisonSettings]
  );

  // Build legend data in TickerChartLegendData format
  const tickerLegendData: TickerChartLegendData[] = useMemo(() => {
    if (!comparisonData?.chartData) return [];

    return comparisonData.chartData.map((ticker, index) => ({
      id: ticker.id,
      name: ticker.name || ticker.id,
      color: getChartTickerColor(index),
      checked: selectedLegends.has(ticker.id),
      // First item (watchlist) is always disabled
      disabled: index === 0,
    }));
  }, [comparisonData, selectedLegends]);

  // ✅ NOW safe to do early return AFTER all hooks
  if (!comparisonData?.chartData) return null;

  // Reuse original TickerChartLegend component
  return (
    <TickerChartLegend
      className={className}
      hoveredDataPoint={hoveredDataPoint}
      tickerLegendData={tickerLegendData}
      handleTickerSelectionChange={handleTickerSelectionChange}
      handleTickerRemoval={handleTickerRemoval}
      enableTickerRemoval={enableTickerRemoval}
      applyInteractiveStyles={applyInteractiveStyles}
      // Watchlist itself should not be removable
      allowFirstTickerRemoval={false}
    />
  );
}
